import React, { useState } from 'react';
import { ConnectionModel } from '../../connection.model';
import ValidationError from './validation-error';
import CustomButton from '../../../../shared/custom-button/custom-button';
import smartPolicyImg from 'src/assets/svg/smart-policy-action.png';
import { ECMActionType } from 'src/utils/enums';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../../store';
import { EventType } from 'src/dtos/analytics.dto';
import { SmartProtectionContent } from '@src/components/features/protection/smart-protection-modal/smart-protection-content';

interface SmartPolicyComponentProps {
    selectedConnections: Map<number, ConnectionModel>;
    setSelectedConnections?: React.Dispatch<React.SetStateAction<Map<number, ConnectionModel>>>;
    showToast: (actionType: ECMActionType, count: number, toApply?: boolean, soakDays?: number) => void;
}

const SmartPolicyComponent: React.FC<SmartPolicyComponentProps> = observer(({ selectedConnections, setSelectedConnections, showToast }) => {
    const { customerStore } = useStores();
    const [isLoading, setIsLoading] = useState(false);

    const allConnections = Array.from(selectedConnections.values());

    // Partition into eligible and ineligible connections
    const ineligibleEntries = allConnections.filter(conn => !conn.isSmartEligible);
    const eligibleConnections = allConnections.filter(conn => conn.isSmartEligible);
    const analysisDaysLeft = eligibleConnections.length > 0
        ? Math.max(...eligibleConnections.map(conn => conn.analysisDaysLeft))
        : 0;
    const hasIneligible = ineligibleEntries.length > 0;
    const hasEligible = eligibleConnections.length > 0;
    const isApplyDisabled = !hasEligible || hasIneligible;

    const handleDeselectConnection = () => {
        if (setSelectedConnections) {
            const newSelectedConnections = new Map(
                Array.from(selectedConnections.entries()).filter(([, connection]) => !ineligibleEntries.includes(connection))
            );

            setSelectedConnections(newSelectedConnections);
        }
    };


    const handleApply = async () => {
        // Apply only to eligible connections
        const destinationServiceIdList = eligibleConnections.map(conn => conn.id);
        try {
            setIsLoading(true);
            await customerStore.setSmartPolicy(destinationServiceIdList);
            customerStore.sendGeneralEvent(
                EventType.LinkClicked,
                {
                    button: 'createSmartDraft',
                    number_of_sid: destinationServiceIdList.length,
                    location: 'smartPolicyModal',
                }
            );
            showToast(ECMActionType.SmartPolicy, destinationServiceIdList.length, undefined, analysisDaysLeft || undefined);
            setSelectedConnections?.(new Map());
        } catch {
            showToast(ECMActionType.SmartPolicy, 0);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="smart-policy-component">
            <div className="action-type-content">
                {/* Ineligible connections warning with per-connection reasons */}
                {hasIneligible && (
                    <div className="smart-policy-ineligible-section">
                        <ValidationError
                            message="Smart protection can't be applied to some selected connections."
                            deselectedConnections={ineligibleEntries}
                            onDeselectConnection={setSelectedConnections ? handleDeselectConnection : undefined} />
                    </div>
                )}

                <div className="action-type-title">Smart Protection</div>
                <div className="action-type-desc">Automated & personalized</div>

                <SmartProtectionContent analysisDaysLeft={eligibleConnections.length > 0 ? analysisDaysLeft : undefined} />

                <img src={smartPolicyImg} alt="Smart Policy" className="smart-policy-image"/>
            </div>
            <div className="action-type-footer-btn">
                <CustomButton
                    label="Create Smart draft"
                    className={`primary-btn action-btn ${isLoading ? 'loading' : ''}`}
                    onClick={handleApply}
                    isDisabled={selectedConnections.size === 0 || isApplyDisabled || isLoading}
                />
            </div>
        </div>
    );
});

export default SmartPolicyComponent;

